<template>
  <section class="card">
    <form
      class="form"
      aria-label="Filter"
      @submit.prevent="resetFilters"
    >
      <div class="field">
        <label for="subjectSelect">Pflichtangebot</label>
        <select
          id="subjectSelect"
          v-model="subject"
          class="input"
          :disabled="loading"
          @change="fetchData"
        >
          <option
            v-for="s in subjects"
            :key="s"
            :value="s"
          >
            {{ s }}
          </option>
        </select>
      </div>

      <div class="field">
        <label for="statusFilterSelect">Status</label>
        <select
          id="statusFilterSelect"
          v-model="statusFilter"
          class="input"
          :disabled="loading"
        >
          <option value="all">
            alle Schüler
          </option>
          <option value="present">
            anwesend
          </option>
          <option value="absent">
            abwesend
          </option>
        </select>
      </div>

      <div class="field actions-field">
        <button
          class="btn"
          type="button"
          :disabled="loading"
          @click="resetFilters"
        >
          Reset
        </button>
      </div>

      <p
        v-if="error"
        class="notice notice-danger"
        role="alert"
      >
        {{ error }}
      </p>
    </form>

    <div
      v-if="events.length === 0"
      class="mt-12 muted"
    >
      Keine passenden Unterrichtsstunden gefunden (heute wird nicht berücksichtigt).
    </div>

    <div
      v-else
      class="mt-12 table-wrap"
    >
      <table class="table">
        <thead>
          <tr>
            <th class="th-student">
              Schülername
            </th>

            <th
              v-for="ev in events"
              :key="ev.id"
              class="th-event text-center is-sortable"
              role="button"
              tabindex="0"
              :aria-label="`Nach Anwesenheit sortieren (${eventSort.eventId === ev.id ? (eventSort.direction === 'desc' ? 'anwesend zuerst' : 'abwesend zuerst') : 'anwesend zuerst'})`"
              @click="toggleSortByEvent(ev.id)"
              @keydown.enter.prevent="toggleSortByEvent(ev.id)"
              @keydown.space.prevent="toggleSortByEvent(ev.id)"
            >
              <div class="th-sub">
                {{ ev.topic || '—' }}
                <span
                  class="sort-indicator"
                  aria-hidden="true"
                >
                  <span :class="['caret', 'caret-up', eventSort.eventId === ev.id && eventSort.direction === 'asc' ? 'active' : '']">▲</span>
                  <span :class="['caret', 'caret-down', eventSort.eventId === ev.id && eventSort.direction === 'desc' ? 'active' : '']">▼</span>
                </span>
              </div>
              <div class="th-top font-normal">
                {{ ev.rangeLabel || '—' }}
              </div>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="row in displayRows"
            :key="row.studentId"
          >
            <td class="td-student">
              {{ row.studentName }}
            </td>

            <td
              v-for="ev in events"
              :key="ev.id"
              :class="['td-att', isPresent(row, ev.id) ? '':'absent']"
              :title="[isPresent(row, ev.id) ? 'anwesend':'abwesend']"
            >
              <span
                v-if="isPresent(row, ev.id)"
                class="present"
                aria-label="anwesend"
              >
                <i
                  class="pi pi-check"
                  aria-hidden="true"
                />
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script setup>
import {onMounted, ref, computed} from 'vue';
import api from '@/services/api';

const loading = ref(false);
const error = ref('');

const subjects = ref([]);
const subject = ref(null);

const events = ref([]); // [{id, topic, subject, rangeLabel}]
const rows = ref([]);   // [{studentId, studentName, attendance: { [eventId]: boolean }}]

const statusFilter = ref('all'); // 'all' | 'present' | 'absent'
const filteredRows = computed(() => {
  if (statusFilter.value === 'present') {
    return rows.value.filter(row =>
      events.value.length > 0 && events.value.every(ev => isPresent(row, ev.id))
    );
  }
  if (statusFilter.value === 'absent') {
    return rows.value.filter(row =>
      events.value.some(ev => !isPresent(row, ev.id))
    );
  }
  return rows.value;
});

const eventSort = ref({eventId: null, direction: 'desc'}); // 'desc' => anwesend zuerst, 'asc' => abwesend zuerst

function toggleSortByEvent(eventId) {
  if (eventSort.value.eventId === eventId) {
    eventSort.value.direction = eventSort.value.direction === 'asc' ? 'desc' : 'asc';
  } else {
    eventSort.value.eventId = eventId;
    eventSort.value.direction = 'desc';
  }
}

const displayRows = computed(() => {
  const base = filteredRows.value.slice();
  const evId = eventSort.value.eventId;
  if (!evId) return base;
  const dir = eventSort.value.direction === 'asc' ? 1 : -1;
  return base.sort((a, b) => {
    const pa = !!a?.attendance?.[evId];
    const pb = !!b?.attendance?.[evId];
    if (pa === pb) {
      return String(a.studentName).localeCompare(String(b.studentName), 'de');
    }
    return dir * ((pa ? 1 : 0) - (pb ? 1 : 0));
  });
});

function resetFilters() {
  statusFilter.value = 'all';
  eventSort.value = { eventId: null, direction: 'desc' };
  subject.value = null;
  fetchData();
}

function isPresent(row, eventId) {
  return !!row?.attendance?.[eventId];
}

async function fetchData() {
  loading.value = true;
  error.value = '';
  try {
    const params = {
      subject: subject.value || undefined,
      limit: 5
    };
    const {data} = await api.get('/reports/mandatory-last-lessons', {params});

    subjects.value = Array.isArray(data?.subjects) ? data.subjects : [];
    subject.value = data?.selectedSubject ?? (subjects.value[0] ?? null);

    events.value = Array.isArray(data?.events) ? data.events : [];
    rows.value = Array.isArray(data?.rows) ? data.rows : [];

    // Falls die aktuell sortierte Spalte nicht mehr existiert, Sortierung zurücksetzen
    if (eventSort.value?.eventId != null) {
      const exists = events.value.some(e => e.id === eventSort.value.eventId);
      if (!exists) {
        eventSort.value.eventId = null;
      }
    }
  } catch (e) {
    error.value = e?.message || 'Unbekannter Fehler beim Laden der Auswertung.';
    subjects.value = [];
    events.value = [];
    rows.value = [];
  } finally {
    loading.value = false;
  }
}

onMounted(async () => {
  await fetchData();
});
</script>

<style scoped>
.card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 14px;
}

.form {
  display: grid;
  grid-template-columns: 400px 200px auto;
  gap: 12px;
  align-items: end;
}

@media (max-width: 640px) {
  .form {
    grid-template-columns: 1fr;
  }
}

.field {
  margin-bottom: 0;
}

.actions-field {
  display: flex;
  justify-content: flex-start;
  gap: 8px;
}

.input {
  height: 38px;
  padding: 8px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  color: var(--text);
}

.input:focus {
  outline: none;
  box-shadow: 0 0 0 3px var(--focus);
  border-color: var(--primary);
}

.btn {
  height: 40px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  cursor: pointer;
}

.btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.btn-primary {
  background: var(--primary);
  color: #fff;
  border-color: var(--primary);
}

.btn-primary:hover {
  background: var(--primary-600);
  border-color: var(--primary-600);
}

.spinner {
  box-sizing: border-box;
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-top-color: #fff;
  border-radius: 50%;
  margin-right: 6px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.mt-12 {
  margin-top: 12px;
}

.muted {
  color: var(--muted);
}

.table-wrap {
  overflow: auto;
  border: 1px solid var(--border);
  border-radius: 10px;
}

.table {
  width: auto;
  border-collapse: collapse;
  background: var(--surface);
}

thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--surface);
  border-bottom: 1px solid var(--border);
  padding: 8px;
  text-align: left;
  vertical-align: bottom;
  font-weight: 600;
  border-right: 1px solid var(--border);
}

tbody td {
  border-bottom: 1px solid var(--border);
  padding: 6px 8px;
  border-right: 1px solid var(--border);
}

tbody tr:last-child td {
  border-bottom: 0;
}

.th-student,
.td-student {
  width: 260px;
}

.td-student {
  font-weight: normal;
  padding: 3px 8px;
}

.th-event {
  width: 300px;
}

.th-event.is-sortable {
  cursor: pointer;
  user-select: none;
}

.th-event.is-sortable:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--focus) inset;
  border-radius: 4px;
}

.th-event .th-sub {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.sort-indicator {
  display: inline-flex;
  flex-direction: column;
  line-height: 10px;
}

.caret {
  font-size: 10px;
  color: color-mix(in srgb, var(--muted) 30%, var(--surface));
}

.caret.active {
  color: var(--text);
}

.th-student + .th-event {
  background: color-mix(in srgb, var(--success) 25%, var(--surface));
}

.th-top {
  font-size: 12px;
  color: var(--muted);
  line-height: 16px;
}

.th-sub {
  margin-top: 2px;
  font-size: 14px;
  color: var(--text);
  line-height: 18px;
}

.td-att {
  text-align: center;
}

.present {
  align-items: center;
  color: color-mix(in srgb, var(--success) 85%, var(--text));
}

.absent {
  background: color-mix(in srgb, var(--warn) 18%, transparent);
}
</style>
